import React, { useState, useRef, useEffect } from 'react';
import { PlusCircle, ChevronDown, FileText, FolderPlus } from 'lucide-react';
import SearchInput from '../common/SearchInput';
import { STATUS } from '../../constants/appConstants';

/**
 * @description Componente que agrupa os controles de interação para a lista de notificações.
 */
// ⚡ Bolt: Wrapped in React.memo() to prevent unnecessary re-renders of the entire control section
// when other parts of NotificationsPage update (e.g. paginatedNotifications).
const NotificationsControls = React.memo(({
    searchTerm,
    onSearchChange,
    statusFilter,
    onStatusChange,
    onNewClick,
    onNewBulkClick,
    userRole
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const canCreate = userRole === 'administrador' || userRole === 'tecnico';

    const statuses = [
        { value: STATUS.PENDENTE, label: 'Pendentes' },
        { value: STATUS.EM_EXECUCAO, label: 'Em execução' },
        { value: STATUS.A_RESPONDER, label: 'A responder' },
        { value: STATUS.FINALIZADA, label: 'Finalizadas' }
    ];

    // Fechar o menu ao clicar fora dele
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);

    return (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
                <div className="w-full min-w-0 flex-1">
                    <SearchInput
                        value={searchTerm}
                        onChange={onSearchChange}
                        placeholder="Buscar por N° da notificação, técnico, cidade..."
                        className="w-full"
                        aria-label="Buscar notificações"
                        debounceDelay={0}
                    />
                </div>

                <select
                    value={statusFilter}
                    onChange={onStatusChange}
                    className="input-style w-full sm:w-48 sm:shrink-0 py-2 px-4"
                    aria-label="Filtrar por status"
                >
                    <option value="Todos">Todos os Status</option>
                    {statuses.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>

                {canCreate && (
                    <div className="relative w-full sm:w-auto shrink-0" ref={menuRef}>
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="btn-primary flex w-full sm:w-auto items-center justify-center"
                            aria-label="Opções de nova notificação"
                            aria-expanded={isMenuOpen}
                        >
                            <PlusCircle className="mr-2 h-5 w-5" />
                            <span>Nova</span>
                            <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {isMenuOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden animate-fade-in">
                                <button
                                    onClick={() => {
                                        onNewClick();
                                        setIsMenuOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center transition-colors border-b border-slate-100 dark:border-slate-700"
                                >
                                    <FileText className="mr-2 h-4 w-4 text-blue-500" />
                                    Individual
                                </button>
                                <button
                                    onClick={() => {
                                        if (onNewBulkClick) onNewBulkClick();
                                        setIsMenuOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center transition-colors"
                                >
                                    <FolderPlus className="mr-2 h-4 w-4 text-green-500" />
                                    Em massa
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});

export default NotificationsControls;
